import React, { FormEvent, useEffect, useRef, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAdminAuth } from "../../hooks/useAdminAuth";
import { getApiErrorMessage } from "../../store/api/baseApi";
import {
  useFetchAdminProfileQuery,
  useUpdateAdminProfileMutation,
  useUploadAdminAvatarMutation,
  useDeleteAdminAvatarMutation,
  getFullAvatarUrl,
} from "../../store/api/adminApi";
import "./admin.css";

const AdminProfile = () => {
  const { adminUser, updateProfile } = useAdminAuth();
  const { data: serverProfile, refetch: refetchProfile } = useFetchAdminProfileQuery();

  const [name, setName]   = useState(adminUser?.name  ?? "");
  const [phone, setPhone] = useState(adminUser?.phone ?? "");
  const [isEditing, setIsEditing] = useState(false);
  const [saved, setSaved]         = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [avatarHover, setAvatarHover]   = useState(false);
  const [avatarError, setAvatarError]   = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [updateAdminProfile, { isLoading: isSaving }]  = useUpdateAdminProfileMutation();
  const [uploadAvatar,       { isLoading: isUploading }] = useUploadAdminAvatarMutation();
  const [deleteAvatar,       { isLoading: isDeleting }]  = useDeleteAdminAvatarMutation();

  useEffect(() => {
    setName(adminUser?.name  ?? "");
    setPhone(adminUser?.phone ?? "");
  }, [adminUser]);

  useEffect(() => {
    if (!serverProfile) return;
    setName(serverProfile.name   || adminUser?.name  || "");
    setPhone(serverProfile.phone || adminUser?.phone || "");
  }, [serverProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!adminUser) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    setSaved(false);
    try {
      const profile = await updateAdminProfile({
        name,
        phone,
        role: serverProfile?.role || adminUser.role || "Admin",
      }).unwrap();
      updateProfile({ name: profile.name || name, phone: profile.phone || phone, role: profile.role });
      setSaved(true);
      setIsEditing(false);
      window.setTimeout(() => setSaved(false), 2500);
    } catch (apiError) {
      setErrorMessage(getApiErrorMessage(apiError as FetchBaseQueryError));
    }
  };

  const handleCancel = () => {
    setName(serverProfile?.name   || adminUser.name  || "");
    setPhone(serverProfile?.phone || adminUser.phone || "");
    setErrorMessage("");
    setIsEditing(false);
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5_000_000) { setAvatarError("File must be under 5 MB."); return; }
    setAvatarError("");
    try {
      await uploadAvatar(file).unwrap();
      refetchProfile();
    } catch {
      setAvatarError("Upload failed. Please try again.");
    }
  };

  const handleRemoveAvatar = async () => {
    setAvatarError("");
    try {
      await deleteAvatar().unwrap();
      refetchProfile();
    } catch {
      setAvatarError("Could not remove photo.");
    }
  };

  const initial    = adminUser.name.trim().charAt(0).toUpperCase() || "A";
  const displayCity = serverProfile?.city  || "";
  const isVerified  = serverProfile?.isVerified ?? false;
  const displayName = name  || adminUser.name;
  const avatarSrc   = getFullAvatarUrl(serverProfile?.avatarUrl ?? null);
  const avatarBusy  = isUploading || isDeleting;

  /* ── Avatar circle ─────────────────────────────────────── */
  const AvatarCircle = ({ size }: { size: "lg" | "md" }) => (
    <div
      className={`ap-avatar-wrap ap-avatar-${size}`}
      onMouseEnter={() => setAvatarHover(true)}
      onMouseLeave={() => setAvatarHover(false)}
    >
      <div
        className={`ap-avatar-circle${avatarSrc ? " ap-avatar-has-img" : ""}`}
        onClick={() => !avatarBusy && fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
        aria-label="Change profile photo"
      >
        {avatarBusy ? (
          <span className="spinner-border spinner-border-sm text-white" role="status" />
        ) : avatarSrc ? (
          <img src={avatarSrc} alt="Profile" className="ap-avatar-img" />
        ) : (
          initial
        )}
      </div>
      {avatarHover && !avatarBusy && (
        <div className="ap-avatar-overlay" onClick={() => fileInputRef.current?.click()}>
          <i className="bi bi-camera-fill" />
          <span>Change</span>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        aria-label="Upload profile photo"
        title="Upload profile photo"
        className="ap-file-input"
        onChange={handleAvatarChange}
      />
    </div>
  );

  return (
    <section className="admin-page ap-page">

      {/* ── Header card ─────────────────────────────────────── */}
      <div className="ap-header-card">
        <div className="ap-header-avatar-col">
          <AvatarCircle size="lg" />

          {/* Photo upload controls — always visible below avatar */}
          <div className="ap-photo-controls">
            {avatarError && (
              <div className="ap-avatar-error">
                <i className="bi bi-exclamation-triangle-fill" /> {avatarError}
              </div>
            )}
            <div className="ap-photo-btn-row">
              <button
                type="button"
                className="ap-btn-upload"
                disabled={avatarBusy}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading
                  ? <><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Uploading…</>
                  : <><i className="bi bi-camera" /> {avatarSrc ? "Change" : "Upload"}</>}
              </button>
              {avatarSrc && (
                <button
                  type="button"
                  className="ap-btn-remove"
                  disabled={avatarBusy}
                  onClick={handleRemoveAvatar}
                >
                  {isDeleting
                    ? <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    : <i className="bi bi-trash3" />}
                </button>
              )}
            </div>
            <span className="ap-photo-hint">JPG, PNG or WebP · max 5 MB</span>
          </div>
        </div>

        <div className="ap-header-info">
          <div className="ap-header-name-row">
            <h4 className="ap-header-name">{displayName}</h4>
            {isVerified && (
              <span className="ap-verified-badge">
                <i className="bi bi-patch-check-fill" /> Verified
              </span>
            )}
          </div>
          <div className="ap-header-email">
            <i className="bi bi-envelope" /> {adminUser.email}
          </div>
          {displayCity && (
            <div className="ap-header-city">
              <i className="bi bi-geo-alt" /> {displayCity}
            </div>
          )}
          <div className="ap-header-badges">
            <span className="ap-badge ap-badge-info">Full Admin</span>
            {serverProfile?.id && (
              <span className="ap-badge ap-badge-neutral">ID #{serverProfile.id}</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Success toast (persists after save) ─────────────── */}
      {saved && !isEditing && (
        <div className="ap-save-toast">
          <i className="bi bi-check-circle-fill" /> Profile updated successfully.
        </div>
      )}

      {/* ── Overview OR Edit ─────────────────────────────────── */}
      <div className="ap-body">
        {!isEditing ? (

          /* ════ OVERVIEW ════ */
          <div className="ap-overview-grid ap-overview-single">

            {/* Read-only details */}
            <div className="ap-overview-main">
              <div className="ap-card">
                <div className="ap-card-header">
                  <i className="bi bi-person-lines-fill ap-card-icon" />
                  <span>Profile Details</span>
                </div>
                <div className="ap-card-body">
                  <div className="row g-3">

                    <div className="col-sm-6">
                      <label className="ap-ro-label" htmlFor="ro-name">Full Name</label>
                      <div className="ap-ro-input-wrap">
                        <i className="bi bi-person ap-ro-icon" />
                        <input
                          id="ro-name"
                          className="ap-ro-input"
                          value={displayName}
                          readOnly
                          tabIndex={-1}
                          aria-label="Full name (read only)"
                        />
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <label className="ap-ro-label" htmlFor="ro-email">Email Address</label>
                      <div className="ap-ro-input-wrap">
                        <i className="bi bi-envelope ap-ro-icon" />
                        <input
                          id="ro-email"
                          className="ap-ro-input"
                          value={adminUser.email}
                          readOnly
                          tabIndex={-1}
                          aria-label="Email address (read only)"
                        />
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <label className="ap-ro-label" htmlFor="ro-phone">Phone</label>
                      <div className="ap-ro-input-wrap">
                        <i className="bi bi-telephone ap-ro-icon" />
                        <input
                          id="ro-phone"
                          className="ap-ro-input"
                          value={phone || adminUser.phone || "—"}
                          readOnly
                          tabIndex={-1}
                          aria-label="Phone (read only)"
                        />
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <label className="ap-ro-label" htmlFor="ro-city">City</label>
                      <div className="ap-ro-input-wrap">
                        <i className="bi bi-geo-alt ap-ro-icon" />
                        <input
                          id="ro-city"
                          className="ap-ro-input"
                          value={displayCity || "—"}
                          readOnly
                          tabIndex={-1}
                          aria-label="City (read only)"
                        />
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <label className="ap-ro-label" htmlFor="ro-status">Account Status</label>
                      <div className="ap-ro-input-wrap">
                        <i className={`bi ${isVerified ? "bi-shield-check" : "bi-shield"} ap-ro-icon`} />
                        <input
                          id="ro-status"
                          className="ap-ro-input"
                          value={isVerified ? "Verified" : "Pending Verification"}
                          readOnly
                          tabIndex={-1}
                          aria-label="Account status (read only)"
                        />
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <label className="ap-ro-label" htmlFor="ro-access">Access Level</label>
                      <div className="ap-ro-input-wrap">
                        <i className="bi bi-lock ap-ro-icon" />
                        <input
                          id="ro-access"
                          className="ap-ro-input"
                          value="Full Admin"
                          readOnly
                          tabIndex={-1}
                          aria-label="Access level (read only)"
                        />
                      </div>
                    </div>

                  </div>

                  {/* Edit Profile button — only here in overview */}
                  <div className="ap-overview-footer">
                    <button
                      type="button"
                      className="ap-btn-edit-profile"
                      onClick={() => setIsEditing(true)}
                    >
                      <i className="bi bi-pencil-square" /> Edit Profile
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>

        ) : (

          /* ════ EDIT FORM ════ */
          <div className="ap-overview-grid ap-overview-single">
            <div className="ap-overview-main">
              <div className="ap-card">
                <div className="ap-card-header">
                  <div className="ap-card-header-icon-box">
                    <i className="bi bi-pencil-square" />
                  </div>
                  <div>
                    <div className="ap-card-header-title">Edit Profile</div>
                    <div className="ap-card-header-sub">Update your name and contact details</div>
                  </div>
                </div>

                <div className="ap-card-body">
                  {errorMessage && (
                    <div className="alert alert-danger py-2 mb-3">{errorMessage}</div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">

                      <div className="col-sm-6">
                        <label className="ap-ro-label" htmlFor="ep-name">Full Name</label>
                        <div className="ap-ro-input-wrap">
                          <i className="bi bi-person ap-ro-icon" />
                          <input
                            id="ep-name"
                            className="ap-ro-input ap-ro-input-edit"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoComplete="name"
                          />
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <label className="ap-ro-label" htmlFor="ep-email">Email Address</label>
                        <div className="ap-ro-input-wrap">
                          <i className="bi bi-envelope ap-ro-icon" />
                          <i className="bi bi-lock ap-ro-icon-right" />
                          <input
                            id="ep-email"
                            className="ap-ro-input ap-ro-input-locked"
                            value={adminUser.email}
                            readOnly
                            tabIndex={-1}
                            aria-label="Email cannot be changed"
                          />
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <label className="ap-ro-label" htmlFor="ep-phone">Phone</label>
                        <div className="ap-ro-input-wrap">
                          <i className="bi bi-telephone ap-ro-icon" />
                          <input
                            id="ep-phone"
                            className="ap-ro-input ap-ro-input-edit"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            autoComplete="tel"
                          />
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <label className="ap-ro-label" htmlFor="ep-city">City</label>
                        <div className="ap-ro-input-wrap">
                          <i className="bi bi-geo-alt ap-ro-icon" />
                          <input
                            id="ep-city"
                            className="ap-ro-input ap-ro-input-locked"
                            value={displayCity || "—"}
                            readOnly
                            tabIndex={-1}
                            aria-label="City cannot be changed here"
                          />
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <label className="ap-ro-label" htmlFor="ep-status">Account Status</label>
                        <div className="ap-ro-input-wrap">
                          <i className={`bi ${isVerified ? "bi-shield-check" : "bi-shield"} ap-ro-icon`} />
                          <input
                            id="ep-status"
                            className="ap-ro-input ap-ro-input-locked"
                            value={isVerified ? "Verified" : "Pending Verification"}
                            readOnly
                            tabIndex={-1}
                            aria-label="Account status"
                          />
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <label className="ap-ro-label" htmlFor="ep-access">Access Level</label>
                        <div className="ap-ro-input-wrap">
                          <i className="bi bi-lock ap-ro-icon" />
                          <input
                            id="ep-access"
                            className="ap-ro-input ap-ro-input-locked"
                            value="Full Admin"
                            readOnly
                            tabIndex={-1}
                            aria-label="Access level"
                          />
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="ap-form-footer">
                          <button type="submit" disabled={isSaving} className="ap-btn-save">
                            {isSaving
                              ? <><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Saving…</>
                              : <><i className="bi bi-check2-circle" /> Save Changes</>}
                          </button>
                          <button type="button" className="ap-btn-cancel" onClick={handleCancel}>
                            Cancel
                          </button>
                        </div>
                      </div>

                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminProfile;
